import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Typography } from "@mui/material";
import {
  Checkbox,
  Field,
  FieldsContainer,
  Form,
  Spacer,
  TextField,
  TextFieldProps,
} from "common";
import I18n from "i18n-js";
import { useState } from "react";
import { FieldCoordinates, useFormEditor } from "./FormEditorDataProvider";
import { FieldItem, FieldType } from "./types";

function uniqueId() {
  return Math.floor(Math.random() * Date.now()).toString(16);
}
function defaultField() {
  return {
    id: uniqueId(),
    type: FieldType.text,
    name: "Text Field",
  };
}

type ArrayTextFieldProps = {} & TextFieldProps;
function ArrayTextField({ onChange, value, ...rest }: ArrayTextFieldProps) {
  function handleChange(evt: any) {
    if (evt.target.value) {
      onChange && onChange({ target: { value: evt.target.value.split("\n") } });
    } else {
      onChange && onChange({ target: { value: null } });
    }
  }
  const stringValue = Array.isArray(value)
    ? (value as any).join("\n")
    : undefined;
  return <TextField {...rest} onChange={handleChange} value={stringValue} />;
}

export interface FieldEditorProps {
  field: FieldItem;
  coordinates: FieldCoordinates;
  onAdvancedToggle?: () => void;
}

export default function FieldEditor({
  field,
  coordinates,
  onAdvancedToggle,
}: FieldEditorProps) {
  const { handleFieldChange } = useFormEditor();
  const [showAdvanced, setShowAdvanced] = useState(false);

  function typeIn(types: FieldType[]) {
    return field ? types.includes(field.type) : false;
  }

  const handleAdvancedToggle = () => {
    setShowAdvanced(!showAdvanced);
    // Call the callback after state update to reposition popover
    setTimeout(() => onAdvancedToggle?.(), 0);
  };

  return (
    <Box
      sx={{
        minWidth: "150px",
        maxWidth: "300px",
        p: 2,
      }}
    >
      <Typography variant="h5">
        {I18n.t("FormEditor.fieldEditor.title")}
      </Typography>
      <Spacer h={2} />

      <Form
        item={field}
        onChange={(item: any, id: string) =>
          handleFieldChange(coordinates, item)
        }
        translationCategory="FormEditor.attributes"
        translationHelpersCategory="FormEditor.helpers"
        translationPlaceholdersCategory="FormEditor.placeholders"
      >
        <FieldsContainer spacing={1.5}>
          <Field fullWidth>
            <TextField id="name" />
          </Field>
          {/*<Field fullWidth>
            <Autocomplete id="type" options={enumToOptions(FieldType)} />
      </Field>*/}
          {typeIn([FieldType.multiline_text]) && (
            <>
              {/*<Field fullWidth>
                <Checkbox id="multiline" />
              </Field>*/}
              {!!field?.multiline && (
                <Field fullWidth>
                  <TextField id="rows" />
                </Field>
              )}
            </>
          )}
          {typeIn([FieldType.select, FieldType.radio_group]) && (
            <>
              <Field fullWidth>
                <ArrayTextField id="options" multiline minRows={3} />
              </Field>
              <Field fullWidth>
                <Checkbox id="multiple" />
              </Field>
            </>
          )}
          {!typeIn([
            FieldType.paragraph,
            FieldType.image,
            FieldType.signature,
          ]) && (
            <Field fullWidth>
              <TextField id="placeholder" />
            </Field>
          )}
          <Field fullWidth>
            <TextField id="helperText" />
          </Field>
          <Field fullWidth>
            <Checkbox id="required" />
          </Field>

          <Button
            onClick={handleAdvancedToggle}
            variant="text"
            startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              color: "text.secondary",
              fontSize: "0.875rem",
              backgroundColor: "transparent!important",
              boxShadow: "none!important",
            }}
            fullWidth
          >
            {I18n.t("FormEditor.fieldEditor.advanced")}
          </Button>

          {showAdvanced && (
            <>
              <Field fullWidth>
                <TextField id="width" />
              </Field>
              {typeIn([FieldType.text]) && (
                <Field fullWidth>
                  <TextField id="mask" />
                </Field>
              )}
              <Field fullWidth>
                <Checkbox id="full_width" />
              </Field>
            </>
          )}
        </FieldsContainer>
      </Form>
    </Box>
  );
}
